import { format, formatDistanceToNow } from 'date-fns';
import {
    Enums,
    formatDuration,
    HelpTooltip,
    Loader,
    softwareVersionToString,
    Tooltip,
    toTimezoneAndFormat,
} from 'Mri.Web.Common';
import * as React from 'react';
import { DisplayCellWrapper } from './DisplayCellWrapper';
import {
    AccountCell,
    BooleanMeasurementCell,
    CommentsCell,
    ContractCell,
    getStatusColor,
    IDisplayCellMeta,
    InfiniteTouchCell,
    MeasurementCell,
    MeasurementSourceCell,
    NewDisplayEventCountCell,
    OpenTicketCountCell,
    ScreenSnapshotCell,
    SerialNumberCell,
    SoftwareUpdateStatusCell,
    VideoInputOutputCell,
    VideoRXTXCell,
    WeatherCodeMeasurementCell,
    ServiceCell,
    AdvancedWarrantyAlertsCell,
    ServiceCOCell,
    PowerGovernorSourceCell,
    AddressCell,
    MeasurementSourceSpan,
    NewDisplayAlertCountCell
} from './DisplayCells';
import { DisplayViewTypes } from '../DisplayFilters/DisplayViewSelector';
import { BsArrowClockwise } from '@react-icons/all-files/bs/BsArrowClockwise';

export const displayTelemetryCells: IDisplayCellMeta[] = [
    {
        id: 'Row #',
        field: null,
        render: ({ rowNumber, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="Row #" className="align-middle">
                {rowNumber}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'Serial Number',
        field: 'SerialNumber',
        render: props => <SerialNumberCell {...props} />,
    },
    {
        id: 'PopVu™',
        field: null,
        render: props => <ScreenSnapshotCell {...props} />,
        afterHeaderRender: vm =>
            !vm.refreshingSnapshots ? (
                <Tooltip message="Refresh Screen Snapshots">
                    <div className="d-inline-block mx-1 cursor-pointer" onClick={vm.refreshScreenshotsAsync}>
                        <BsArrowClockwise style={{ fontSize: 19 }} className="" />
                    </div>
                </Tooltip>
            ) : (
                <Tooltip message="Refreshing Screen Snapshots">
                    <div className="d-inline-block mx-1">
                        <Loader className="" loading={true} showSpinner />
                    </div>
                </Tooltip>
            ),
    },
    {
        id: 'InfiniteTouch®',
        field: null,
        render: props => <InfiniteTouchCell {...props} />,
        afterHeaderRender: vm =>
            !vm.refreshingSnapshots ? (
                <Tooltip message="Refresh Snapshots">
                    <div className="d-inline-block mx-1 cursor-pointer" onClick={vm.refreshScreenshotsAsync}>
                        <BsArrowClockwise style={{ fontSize: 19 }} className="" />
                    </div>
                </Tooltip>
            ) : (
                <Tooltip message="Refreshing Snapshots">
                    <div className="d-inline-block mx-1">
                        <Loader className="" loading={true} showSpinner />
                    </div>
                </Tooltip>
            ),
    },

    // GENERAL //

    {
        id: 'Unit Serial Number',
        group: 'General',
        field: 'Display.UnitSerialNumber',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="Unit Serial Number">
                {display.unitSerialNumber ?? '-'}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'Role In Unit',
        group: 'General',
        field: 'ActiveDisplayStatus.LastKnownPoll?.DisplayStatusPoll?.SystemInfo?.Value.roleInUnit',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="Role In Unit">
                {display.roleInUnit ? Enums.toString(Enums.RoleInUnit, display.roleInUnit) : '-'}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'Display ID',
        group: 'General',
        field: 'Display.DisplayConnectionData?.CustomerDisplayID',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="Display ID">
                {display.displayConnectionData?.customerDisplayID ?? '-'}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'Service Level',
        group: 'General',
        field: 'Contract?.ServiceLevel',
        render: props => <ServiceCell {...props} />,
    },
    {
        id: 'Advanced Warranty Alerts',
        group: 'General',
        field: 'AdvancedWarrantyAlerts',
        render: props => <AdvancedWarrantyAlertsCell {...props} />,
    },
    {
        id: 'Site ID',
        group: 'General',
        field: 'UnitLocation?.CustomerSiteID',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="Site ID">
                {display.unitLocation?.customerSiteID ?? '-'}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'Account',
        group: 'General',
        field: 'Display.Account',
        render: props => <AccountCell {...props} />,
    },
    {
        id: 'Comments',
        group: 'General',
        field: 'Display.Comments',
        render: props => <CommentsCell {...props} />,
    },
    {
        id: 'Address',
        group: 'General',
        field: 'UnitLocation?.Address1',
        render: props => <AddressCell {...props} />,
    },
    //{
    //    id: 'Address',
    //    group: 'General',
    //    field: 'UnitLocation?.Address1',
    //    render: ({ display, vm }) => (
    //        <DisplayCellWrapper vm={vm} cellId="Address">
    //            {display.fullAddress
    //                ? display.fullAddress +
    //                (display?.measurements?.find(s => s.id == Mri.WebApi.MeasurementId.GpsLatitude)?.floatValue
    //                    ? ' (GPS)'
    //                    : display?.configPoll?.geoVuConfig?.latitude
    //                        ? ' (Config)'
    //                        : ' (SmartVu)')
    //                : '-'}
    //        </DisplayCellWrapper>
    //    ),
    //},
    {
        id: 'Tickets',
        group: 'General',
        field: 'OpenTicketCount',
        render: props => <OpenTicketCountCell {...props} />,
    },
    {
        id: 'Alerts',
        group: 'General',
        field: 'DisplayAlertCount',
        render: props => <NewDisplayAlertCountCell {...props} />,
    },
    {
        id: 'New Events',
        group: 'General',
        field: 'NewDisplayEventCount',
        render: props => <NewDisplayEventCountCell {...props} />,
    },
    {
        id: 'Status',
        group: 'General',
        field: 'StatusRank',
        render: ({ display, vm }) => {
            return (
                <DisplayCellWrapper vm={vm} cellId="Status">
                    <HelpTooltip
                        subjects={['Downtime Incidents', 'Display Statuses']}
                        name={display.statusTooltip}
                        underline
                        placement="top"
                        className={getStatusColor(display, vm.isHovered(display)) + ' d-block'}
                    >
                        {vm.viewType == DisplayViewTypes.TableExtraSmall && (
                            <div className="d-inline-block display-row-events-small">
                                {display.statusMessage.split('\n').map((s, idx) => (
                                    <div key={idx}>{s}</div>
                                ))}
                            </div>
                        )}
                        {vm.viewType != DisplayViewTypes.TableExtraSmall && (
                            <div className="d-inline-block display-row-events">
                                {display.statusMessage.split('\n').map((s, idx) => (
                                    <div key={idx}>{s}</div>
                                ))}
                            </div>
                        )}
                    </HelpTooltip>
                </DisplayCellWrapper>
            );
        },
    },

    // SYSTEM //
    {
        id: 'WebApi Kicks',
        group: 'System',
        field: 'Display.WebApiKickDisabled',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="WebApi Kicks">
                {display.webApiKickDisabled ? 'Disabled' : 'Enabled'}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'City',
        group: 'System',
        field: 'UnitLocation?.City',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="City">
                {display.unitLocation?.city ?? '-'}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'State Or Province',
        group: 'System',
        field: 'UnitLocation?.StateOrProvince',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="State Or Province">
                {display.unitLocation?.stateOrProvince ?? '-'}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'Country',
        group: 'System',
        field: 'UnitLocation?.Country',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="Country">
                {display.unitLocation?.country ?? '-'}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'Uptime',
        group: 'System',
        field: 'ActiveDisplayStatus.LastKnownPoll?.DisplayStatusPoll?.Uptime',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="Uptime" className="no-wrap">
                {display.displayStatus == Mri.DisplayStatuses.DisplayStatus.NocCouldNotConnect ? '-' : (display.uptime ? formatDuration(display.uptime) : '-')}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'Ship Date',
        group: 'System',
        field: 'ShippedDate',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="Ship Date">
                {display.shippedDate ? toTimezoneAndFormat(display.shippedDate, 'MM/dd/yyyy', 'UTC') : '-'}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'Contract',
        group: 'System',
        field: 'Display.Contract',
        render: props => <ContractCell {...props} />,
    },
    {
        id: 'Service CO',
        group: 'System',
        field: 'Display.Contract',
        render: props => <ServiceCOCell {...props} />,
    },
    {
        id: 'Last Poll Time',
        group: 'System',
        field: 'DateTime(ActiveDisplayStatus.LastKnownPoll?.PollTime)',
        render: ({ display, vm }) => {
            const pollTime = display.pollTime;
            const message = pollTime
                ? pollTime < new Date()
                    ? formatDistanceToNow(pollTime, { addSuffix: true })
                    : '< 1 min'
                : '-';

            return (
                <DisplayCellWrapper vm={vm} cellId="Last Poll Time">
                    {message}
                </DisplayCellWrapper>
            );
        },
    },
    {
        id: 'IP Address',
        group: 'System',
        field: 'Display.DisplayConnectionData?.DisplayBoardIP',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="IP Address">
                {display.displayConnectionData?.displayBoardIP ?? '-'}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'Update Status',
        group: 'System',
        field: 'SoftwareUpdateStatusPoll?.State',
        render: props => <SoftwareUpdateStatusCell {...props} />,
    },
    {
        id: 'Part Number',
        group: 'System',
        field: 'Display.PartNumber',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="Part Number">
                {display.partNumber ?? '-'}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'Model Number',
        group: 'System',
        field: 'Display.ModelNumber',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="Model Number">
                {display.modelNumber ?? '-'}
            </DisplayCellWrapper>
        ),
    },

    // TimeZone //
    {
        id: 'Current Time',
        group: 'Time/Timezone',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="Current Time" className="no-wrap">
                {display.currentTime ? format(display.currentTime, 'EEEE, MMMM do yyyy, h:mm:ss a') : '-'}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'Timezone',
        group: 'Time/Timezone',
        field: 'ActiveDisplayStatus.LastKnownPoll?.DisplayStatusPoll?.Timezone',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="Timezone" className="no-wrap">
                {display.timezone?.split('_').join(' ') ?? '-'}
            </DisplayCellWrapper>
        ),
    },
    //{
    //    id: 'Description',
    //    group: 'System',
    //    field: 'Display.Description',
    //    render: ({ display, vm }) => (
    //        <DisplayCellWrapper vm={vm} cellId="Description">
    //            {display.description}
    //        </DisplayCellWrapper>
    //    ),
    //},

    // Software //
    {
        id: 'Mri Software',
        group: 'Software',
        field: 'DisplaySoftware?.MriSoftwareVersion',
        shortHeaderLabel: 'Mri',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="Mri Software">
                {softwareVersionToString(display.displaySoftware?.mriSoftwareVersion)}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'UBL',
        group: 'Software',
        field: 'DisplaySoftware?.BootLoaderSoftwareVersion',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="UBL">
                {softwareVersionToString(display.displaySoftware?.bootLoaderSoftwareVersion)}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'UBOOT',
        group: 'Software',
        field: 'DisplaySoftware?.BootSoftwareVersion',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="UBOOT">
                {softwareVersionToString(display.displaySoftware?.bootSoftwareVersion)}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'Kernel',
        group: 'Software',
        field: 'DisplaySoftware?.KernelSoftwareVersion',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="Kernel">
                {softwareVersionToString(display.displaySoftware?.kernelSoftwareVersion)}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'Root',
        group: 'Software',
        field: 'DisplaySoftware?.RootSoftwareVersion',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="Root">
                {softwareVersionToString(display.displaySoftware?.rootSoftwareVersion)}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'Tile Driver 1',
        group: 'Software',
        //field: 'DisplaySoftware?.TileDriverVersions[0]',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="Tile Driver 1">
                {softwareVersionToString(display.displaySoftware?.tileDriverVersions[0])}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'Tile Driver 2',
        group: 'Software',
        //field: 'DisplaySoftware?.TileDriverVersions[1]',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="Tile Driver 2">
                {softwareVersionToString(display.displaySoftware?.tileDriverVersions[1])}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'Tile Driver 3',
        group: 'Software',
        //field: 'DisplaySoftware?.TileDriverVersions[2]',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="Tile Driver 3">
                {softwareVersionToString(display.displaySoftware?.tileDriverVersions[2])}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'Tile Driver 4',
        group: 'Software',
        //field: 'DisplaySoftware?.TileDriverVersions[3]',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="Tile Driver 4">
                {softwareVersionToString(display.displaySoftware?.tileDriverVersions[3])}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'InfiniteTouch® Config Version (Primary)',
        group: 'Software',
        field: 'DisplaySoftware?.InfiniteTouchConfigVersionPrimary',
        shortHeaderLabel: 'IT Con (1)',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="InfiniteTouch® Config Version (Primary)">
                {display.displaySoftware?.infiniteTouchConfigVersionPrimary
                    ? softwareVersionToString(display.displaySoftware?.infiniteTouchConfigVersionPrimary)
                    : '-'}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'InfiniteTouch® Firmware Version (Primary)',
        group: 'Software',
        field: 'DisplaySoftware?.InfiniteTouchFirmwareVersionPrimary',
        shortHeaderLabel: 'IT FW (1)',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="InfiniteTouch® Firmware Version (Primary)">
                {display.displaySoftware?.infiniteTouchFirmwareVersionPrimary
                    ? softwareVersionToString(display.displaySoftware?.infiniteTouchFirmwareVersionPrimary)
                    : '-'}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'InfiniteTouch® Config Version (Secondary)',
        group: 'Software',
        field: 'DisplaySoftware?.InfiniteTouchConfigVersionSecondary',
        shortHeaderLabel: 'IT Con (2)',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="InfiniteTouch® Config Version (Secondary)">
                {display.displaySoftware?.infiniteTouchConfigVersionSecondary
                    ? softwareVersionToString(display.displaySoftware?.infiniteTouchConfigVersionSecondary)
                    : '-'}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'InfiniteTouch® Firmware Version (Secondary)',
        group: 'Software',
        field: 'DisplaySoftware?.InfiniteTouchFirmwareVersionSecondary',
        shortHeaderLabel: 'IT FW (2)',
        render: ({ display, vm }) => (
            <DisplayCellWrapper vm={vm} cellId="InfiniteTouch® Firmware Version (Secondary)">
                {display.displaySoftware?.infiniteTouchFirmwareVersionSecondary
                    ? softwareVersionToString(display.displaySoftware?.infiniteTouchFirmwareVersionSecondary)
                    : '-'}
            </DisplayCellWrapper>
        ),
    },
    {
        id: 'Power Governor Source Name',
        group: 'System Power',
        field: 'ActiveDisplayStatus.LastKnownPoll?.DisplayStatusPoll?.PowerGovSourceName',
        render: props => (
            <DisplayCellWrapper vm={props.vm} cellId="Power Governor Source Name" className="no-wrap">
                {props.display.powerGovSourceName ? (
                    <div>
                        <span>{props.display.powerGovSourceName}</span>
                        {(props.display.powerGovSourceName == 'Reduction System Warm' ||
                            props.display.powerGovSourceName == 'Reduction Backlight Warm' ||
                            props.display.powerGovSourceName == 'Reduction Closed Loop Warm') && (
                                <MeasurementSourceSpan
                                    prefixComma
                                    id={Mri.WebApi.MeasurementId.CoolVuZone1Source}
                                    {...props}
                                />
                            )}
                    </div>
                ) : (
                    '-'
                )}
            </DisplayCellWrapper>
        ),
    },

    // MEASUREMENTS ALL AFTER HERE
    // TIME
    {
        id: Mri.WebApi.MeasurementId.GeoVuSunrise,
        group: 'Time/Timezone',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.GeoVuSunrise} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.GeoVuSunset,
        group: 'Time/Timezone',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.GeoVuSunset} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.WxSunrise,
        group: 'Time/Timezone',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.WxSunrise} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.WxSunset,
        group: 'Time/Timezone',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.WxSunset} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.WxUtcOffset,
        group: 'Time/Timezone',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.WxUtcOffset} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sunrise,
        group: 'Time/Timezone',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sunrise} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sunset,
        group: 'Time/Timezone',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sunset} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.LocalTime,
        group: 'Time/Timezone',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.LocalTime} {...props} />,
    },

    // DOOR //
    {
        id: Mri.WebApi.MeasurementId.Side1MagneticSensorStatus,
        group: 'Door',
        render: props => (
            <BooleanMeasurementCell
                trueValue="Open"
                falseValue="Closed"
                id={Mri.WebApi.MeasurementId.Side1MagneticSensorStatus}
                {...props}
            />
        ),
    },
    {
        id: Mri.WebApi.MeasurementId.Side1Latch1Status,
        group: 'Door',
        render: props => (
            <BooleanMeasurementCell
                trueValue="Open"
                falseValue="Closed"
                id={Mri.WebApi.MeasurementId.Side1Latch1Status}
                {...props}
            />
        ),
    },
    {
        id: Mri.WebApi.MeasurementId.Side1Latch2Status,
        group: 'Door',
        render: props => (
            <BooleanMeasurementCell
                trueValue="Open"
                falseValue="Closed"
                id={Mri.WebApi.MeasurementId.Side1Latch2Status}
                {...props}
            />
        ),
    },
    {
        id: Mri.WebApi.MeasurementId.Side2MagneticSensorStatus,
        group: 'Door',
        render: props => (
            <BooleanMeasurementCell
                trueValue="Open"
                falseValue="Closed"
                id={Mri.WebApi.MeasurementId.Side2MagneticSensorStatus}
                {...props}
            />
        ),
    },
    {
        id: Mri.WebApi.MeasurementId.Side2Latch1Status,
        group: 'Door',
        render: props => (
            <BooleanMeasurementCell
                trueValue="Open"
                falseValue="Closed"
                id={Mri.WebApi.MeasurementId.Side2Latch1Status}
                {...props}
            />
        ),
    },
    {
        id: Mri.WebApi.MeasurementId.Side2Latch2Status,
        group: 'Door',
        render: props => (
            <BooleanMeasurementCell
                trueValue="Open"
                falseValue="Closed"
                id={Mri.WebApi.MeasurementId.Side2Latch2Status}
                {...props}
            />
        ),
    },
    {
        id: Mri.WebApi.MeasurementId.AnyDoorOpenStatus,
        group: 'Door',
        render: props => (
            <BooleanMeasurementCell
                trueValue="Open"
                falseValue="Closed"
                id={Mri.WebApi.MeasurementId.AnyDoorOpenStatus}
                {...props}
            />
        ),
    },

    // SYSTEM POWER //
    {
        id: Mri.WebApi.MeasurementId.MaxPowerSupplyTemp,
        group: 'System Power',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.MaxPowerSupplyTemp} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.UnitTotalPower,
        group: 'System Power',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.UnitTotalPower} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.UnitTotalInletAmps,
        group: 'System Power',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.UnitTotalInletAmps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.UnitTotalV48Amps,
        group: 'System Power',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.UnitTotalV48Amps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.SurgeSuppressorFailedStatus,
        group: 'System Power',
        render: props => (
            <BooleanMeasurementCell
                trueValue="Fail"
                falseValue="OK"
                id={Mri.WebApi.MeasurementId.SurgeSuppressorFailedStatus}
                {...props}
            />
        ),
    },
    {
        id: Mri.WebApi.MeasurementId.PowerGovernorSource,
        group: 'System Power',
        render: props => <PowerGovernorSourceCell id={Mri.WebApi.MeasurementId.PowerGovernorSource} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.KilowattHour,
        group: 'System Power',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.KilowattHour} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.UnitKilowattHour,
        group: 'System Power',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.UnitKilowattHour} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.UnitPowerSupplyEfficiency,
        group: 'System Power',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.UnitPowerSupplyEfficiency} {...props} />,
    },

    {
        id: Mri.WebApi.MeasurementId.InletUnitDailyMaxCurrent,
        group: 'System Power',
        render: props => (
            <MeasurementCell fixedCount={2} id={Mri.WebApi.MeasurementId.InletUnitDailyMaxCurrent} {...props} />
        ),
    },
    {
        id: Mri.WebApi.MeasurementId.InletUnitLifetimeMaxCurrent,
        group: 'System Power',
        render: props => (
            <MeasurementCell fixedCount={2} id={Mri.WebApi.MeasurementId.InletUnitLifetimeMaxCurrent} {...props} />
        ),
    },
    {
        id: Mri.WebApi.MeasurementId.AvgInletVolts,
        group: 'System Power',
        render: props => <MeasurementCell fixedCount={2} id={Mri.WebApi.MeasurementId.AvgInletVolts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.TotalInletAmps,
        group: 'System Power',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.TotalInletAmps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.TotalInletWatts,
        group: 'System Power',
        render: props => <MeasurementCell fixedCount={2} id={Mri.WebApi.MeasurementId.TotalInletWatts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Psu12VTotalAmps,
        group: 'System Power',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Psu12VTotalAmps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Psu12VTotalWatts,
        group: 'System Power',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Psu12VTotalWatts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V28TotalAmps,
        group: 'System Power',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V28TotalAmps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V28TotalWatts,
        group: 'System Power',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V28TotalWatts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V48TotalAmps,
        group: 'System Power',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V48TotalAmps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V48TotalWatts,
        group: 'System Power',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V48TotalWatts} {...props} />,
    },

    // PSU 1 //
    {
        id: Mri.WebApi.MeasurementId.Inlet1Volts,
        group: 'PSU 1',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Inlet1Volts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Inlet1Amps,
        group: 'PSU 1',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Inlet1Amps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Inlet1Watts,
        group: 'PSU 1',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Inlet1Watts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.PowerSupply1Efficiency,
        group: 'PSU 1',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PowerSupply1Efficiency} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.PowerSupply1PF,
        group: 'PSU 1',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PowerSupply1PF} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.PrimaryPsu1Vtrim,
        group: 'PSU 1',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsu1Vtrim} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V48Psu1Volts,
        group: 'PSU 1',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V48Psu1Volts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V48Psu1Amps,
        group: 'PSU 1',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V48Psu1Amps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V48Psu1Watts,
        group: 'PSU 1',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V48Psu1Watts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V48Psu1Celsius,
        group: 'PSU 1',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V48Psu1Celsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Psu1_12VVolts,
        group: 'PSU 1',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Psu1_12VVolts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Psu1_12VAmps,
        group: 'PSU 1',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Psu1_12VAmps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Psu1_12VWatts,
        group: 'PSU 1',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Psu1_12VWatts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V12VPsu1VTrim,
        group: 'PSU 1',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V12VPsu1VTrim} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V28Psu1Volts,
        group: 'PSU 1',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V28Psu1Volts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V28Psu1Amps,
        group: 'PSU 1',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V28Psu1Amps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V28Psu1Watts,
        group: 'PSU 1',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V28Psu1Watts} {...props} />,
    },

    // PSU 2 //
    {
        id: Mri.WebApi.MeasurementId.Inlet2Volts,
        group: 'PSU 2',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Inlet2Volts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Inlet2Amps,
        group: 'PSU 2',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Inlet2Amps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Inlet2Watts,
        group: 'PSU 2',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Inlet2Watts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.PowerSupply2Efficiency,
        group: 'PSU 2',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PowerSupply2Efficiency} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.PowerSupply2PF,
        group: 'PSU 2',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PowerSupply2PF} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.PrimaryPsu2Vtrim,
        group: 'PSU 2',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsu2Vtrim} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V48Psu2Volts,
        group: 'PSU 2',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V48Psu2Volts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V48Psu2Amps,
        group: 'PSU 2',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V48Psu2Amps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V48Psu2Watts,
        group: 'PSU 2',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V48Psu2Watts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V48Psu2Celsius,
        group: 'PSU 2',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V48Psu2Celsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Psu2_12VVolts,
        group: 'PSU 2',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Psu2_12VVolts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Psu2_12VAmps,
        group: 'PSU 2',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Psu2_12VAmps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Psu2_12VWatts,
        group: 'PSU 2',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Psu2_12VWatts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V12VPsu2VTrim,
        group: 'PSU 2',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V12VPsu2VTrim} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V28Psu2Volts,
        group: 'PSU 2',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V28Psu2Volts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V28Psu2Amps,
        group: 'PSU 2',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V28Psu2Amps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V28Psu2Watts,
        group: 'PSU 2',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V28Psu2Watts} {...props} />,
    },

    // PSU 3 //
    {
        id: Mri.WebApi.MeasurementId.Inlet3Volts,
        group: 'PSU 3',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Inlet3Volts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Inlet3Amps,
        group: 'PSU 3',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Inlet3Amps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Inlet3Watts,
        group: 'PSU 3',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Inlet3Watts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.PowerSupply3Efficiency,
        group: 'PSU 3',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PowerSupply3Efficiency} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.PowerSupply3PF,
        group: 'PSU 3',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PowerSupply3PF} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.PrimaryPsu3Vtrim,
        group: 'PSU 3',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsu3Vtrim} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V48Psu3Volts,
        group: 'PSU 3',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V48Psu3Volts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V48Psu3Amps,
        group: 'PSU 3',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V48Psu3Amps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V48Psu3Watts,
        group: 'PSU 3',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V48Psu3Watts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V48Psu3Celsius,

        group: 'PSU 3',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V48Psu3Celsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Psu3_12VVolts,
        group: 'PSU 3',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Psu3_12VVolts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Psu3_12VAmps,
        group: 'PSU 3',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Psu3_12VAmps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Psu3_12VWatts,
        group: 'PSU 3',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Psu3_12VWatts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V12VPsu3VTrim,
        group: 'PSU 3',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V12VPsu3VTrim} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V28Psu3Volts,
        group: 'PSU 3',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V28Psu3Volts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V28Psu3Amps,
        group: 'PSU 3',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V28Psu3Amps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V28Psu3Watts,
        group: 'PSU 3',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V28Psu3Watts} {...props} />,
    },

    // PSU 4 //
    {
        id: Mri.WebApi.MeasurementId.Inlet4Volts,
        group: 'PSU 4',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Inlet4Volts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Inlet4Amps,
        group: 'PSU 4',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Inlet4Amps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Inlet4Watts,
        group: 'PSU 4',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Inlet4Watts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.PowerSupply4Efficiency,
        group: 'PSU 4',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PowerSupply4Efficiency} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.PowerSupply4PF,
        group: 'PSU 4',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PowerSupply4PF} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.PrimaryPsu4Vtrim,
        group: 'PSU 4',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsu4Vtrim} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V48Psu4Volts,
        group: 'PSU 4',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V48Psu4Volts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V48Psu4Amps,
        group: 'PSU 4',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V48Psu4Amps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V48Psu4Watts,
        group: 'PSU 4',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V48Psu4Watts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V48Psu4Celsius,
        group: 'PSU 4',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V48Psu4Celsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Psu4_12VVolts,
        group: 'PSU 4',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Psu4_12VVolts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Psu4_12VAmps,
        group: 'PSU 4',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Psu4_12VAmps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Psu4_12VWatts,
        group: 'PSU 4',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Psu4_12VWatts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V12VPsu4VTrim,
        group: 'PSU 4',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V12VPsu4VTrim} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V28Psu4Volts,
        group: 'PSU 4',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V28Psu4Volts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V28Psu4Amps,
        group: 'PSU 4',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V28Psu4Amps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.V28Psu4Watts,
        group: 'PSU 4',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.V28Psu4Watts} {...props} />,
    },

    // CUSTOM PSU //
    {
        id: Mri.WebApi.MeasurementId.CustomAuxPsu1Volts,
        group: 'Custom PSU',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CustomAuxPsu1Volts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.CustomAuxPsu1Amps,
        group: 'Custom PSU',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CustomAuxPsu1Amps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.CustomAuxPsu1Watts,
        group: 'Custom PSU',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CustomAuxPsu1Watts} {...props} />,
    },

    {
        id: Mri.WebApi.MeasurementId.CustomAuxPsu2Volts,
        group: 'Custom PSU',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CustomAuxPsu2Volts} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.CustomAuxPsu2Amps,
        group: 'Custom PSU',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CustomAuxPsu2Amps} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.CustomAuxPsu2Watts,
        group: 'Custom PSU',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CustomAuxPsu2Watts} {...props} />,
    },

    // FANS //
    {
        id: Mri.WebApi.MeasurementId.ClosedLoopPwmLevel,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.ClosedLoopPwmLevel} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.OpenLoopPwmLevel,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.OpenLoopPwmLevel} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.HeatExchangePwmLevel,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.HeatExchangePwmLevel} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.FanBank1PwmLevel,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.FanBank1PwmLevel} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.FanBank2PwmLevel,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.FanBank2PwmLevel} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.FanBank3PwmLevel,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.FanBank3PwmLevel} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Fan1RPM,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan1RPM} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Fan2RPM,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan2RPM} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Fan3RPM,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan3RPM} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Fan4RPM,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan4RPM} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Fan5RPM,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan5RPM} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Fan6RPM,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan6RPM} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Fan7RPM,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan7RPM} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Fan8RPM,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan8RPM} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Fan9RPM,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan9RPM} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Fan10RPM,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan10RPM} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Fan11RPM,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan11RPM} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Fan12RPM,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan12RPM} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Fan13RPM,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan13RPM} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Fan14RPM,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan14RPM} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Fan15RPM,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan15RPM} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Fan16RPM,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan16RPM} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Fan17RPM,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan17RPM} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Fan18RPM,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan18RPM} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Fan19RPM,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan19RPM} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Fan20RPM,
        group: 'Fans',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan20RPM} {...props} />,
    },

    // BACKLIGHT //
    {
        id: Mri.WebApi.MeasurementId.PowerGovernorLimit,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PowerGovernorLimit} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Backlight1Brightness,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Backlight1Brightness} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Backlight1DesiredBrightness,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Backlight1DesiredBrightness} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.AvgBacklight1LightSensor,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.AvgBacklight1LightSensor} {...props} />,
    },

    {
        id: Mri.WebApi.MeasurementId.Backlight1DacLevel,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Backlight1DacLevel} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Backlight1PwmLevel,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Backlight1PwmLevel} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.AmbientLight1,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.AmbientLight1} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Backlight1BlockDimmingState,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Backlight1BlockDimmingState} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Backlight1BlockDimmingAvgLevel,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Backlight1BlockDimmingAvgLevel} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Backlight1BlockDimmingAvgMultiframeLevel,
        group: 'Backlight',
        render: props => (
            <MeasurementCell id={Mri.WebApi.MeasurementId.Backlight1BlockDimmingAvgMultiframeLevel} {...props} />
        ),
    },
    {
        id: Mri.WebApi.MeasurementId.Backlight2Brightness,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Backlight2Brightness} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Backlight2DesiredBrightness,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Backlight2DesiredBrightness} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.AvgBacklight2LightSensor,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.AvgBacklight2LightSensor} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Backlight2DacLevel,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Backlight2DacLevel} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Backlight2PwmLevel,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Backlight2PwmLevel} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.AmbientLight2,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.AmbientLight2} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Backlight2BlockDimmingState,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Backlight2BlockDimmingState} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Backlight2BlockDimmingAvgLevel,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Backlight2BlockDimmingAvgLevel} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Backlight2BlockDimmingAvgMultiframeLevel,
        group: 'Backlight',
        render: props => (
            <MeasurementCell id={Mri.WebApi.MeasurementId.Backlight2BlockDimmingAvgMultiframeLevel} {...props} />
        ),
    },
    {
        id: Mri.WebApi.MeasurementId.Tile1SensorCelsius,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile1SensorCelsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Tile1LightSensor,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile1LightSensor} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Tile2SensorCelsius,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile2SensorCelsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Tile2LightSensor,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile2LightSensor} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Tile3SensorCelsius,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile3SensorCelsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Tile3LightSensor,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile3LightSensor} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Tile4SensorCelsius,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile4SensorCelsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Tile4LightSensor,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile4LightSensor} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Tile5SensorCelsius,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile5SensorCelsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Tile5LightSensor,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile5LightSensor} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Tile6SensorCelsius,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile6SensorCelsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Tile6LightSensor,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile6LightSensor} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Tile7SensorCelsius,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile7SensorCelsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Tile7LightSensor,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile7LightSensor} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Tile8SensorCelsius,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile8SensorCelsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Tile8LightSensor,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile8LightSensor} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Tile9SensorCelsius,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile9SensorCelsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Tile9LightSensor,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile9LightSensor} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Tile10SensorCelsius,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile10SensorCelsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Tile10LightSensor,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile10LightSensor} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.StaticSideBacklightStatus,
        group: 'Backlight',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.StaticSideBacklightStatus} {...props} />,
    },

    // WEATHERVU //
    {
        id: Mri.WebApi.MeasurementId.CloudCoverRatio,
        group: 'WeatherVu',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CloudCoverRatio} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.HumidityLevel,
        group: 'WeatherVu',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.HumidityLevel} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.WeatherCode,
        group: 'WeatherVu',
        render: props => <WeatherCodeMeasurementCell id={Mri.WebApi.MeasurementId.WeatherCode} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.BarometricPressure,
        group: 'WeatherVu',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.BarometricPressure} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.WindSpeed,
        group: 'WeatherVu',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.WindSpeed} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.WindDirection,
        group: 'WeatherVu',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.WindDirection} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.WindGust,
        group: 'WeatherVu',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.WindGust} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.LocalHumidity,
        group: 'WeatherVu',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.LocalHumidity} {...props} />,
    },

    // COOLVU //
    {
        id: Mri.WebApi.MeasurementId.CoolVuZone1Source,
        group: 'CoolVu',
        render: props => <MeasurementSourceCell id={Mri.WebApi.MeasurementId.CoolVuZone1Source} {...props} />,
    },
    //{
    //    id: Mri.WebApi.MeasurementId.CoolVuZone1ChartSource,
    //    group: 'CoolVu',
    //    render: props => <ChartSourceCell id={Mri.WebApi.MeasurementId.CoolVuZone1ChartSource} {...props} />,
    //},
    {
        id: Mri.WebApi.MeasurementId.CoolVuZone1Celsius,
        group: 'CoolVu',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CoolVuZone1Celsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.CoolVuZone2Source,
        group: 'CoolVu',
        render: props => <MeasurementSourceCell id={Mri.WebApi.MeasurementId.CoolVuZone2Source} {...props} />,
    },
    //{
    //    id: Mri.WebApi.MeasurementId.CoolVuZone2ChartSource,
    //    group: 'CoolVu',
    //    render: props => <MeasurementSourceCell id={Mri.WebApi.MeasurementId.CoolVuZone2ChartSource} {...props} />,
    //},
    {
        id: Mri.WebApi.MeasurementId.CoolVuZone2Celsius,
        group: 'CoolVu',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CoolVuZone2Celsius} {...props} />,
    },

    // ENVIRONMENTALS //
    {
        id: Mri.WebApi.MeasurementId.Backlight1Celsius,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Backlight1Celsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Backlight1MaxCelsius,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Backlight1MaxCelsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Backlight2Celsius,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Backlight2Celsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Backlight2MaxCelsius,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Backlight2MaxCelsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.ClosedLoopCelsius,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.ClosedLoopCelsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.ClosedLoopCelsius2,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.ClosedLoopCelsius2} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.PressureDifferential,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PressureDifferential} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.PressureSensorTemperature,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PressureSensorTemperature} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.SealStatus,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.SealStatus} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.LeakDetected,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.LeakDetected} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.FpgaDieCelsius,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.FpgaDieCelsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.ProcessorBoardCelsius,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.ProcessorBoardCelsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.InletSensorCelsius,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.InletSensorCelsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.LocalWeatherCelsius,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.LocalWeatherCelsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.AmbientDeltaCelsius,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.AmbientDeltaCelsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.HumiditySensorCelsius,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.HumiditySensorCelsius} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.HumidityUpper,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.HumidityUpper} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.HumidityLower,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.HumidityLower} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.UpperMetalTemp,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.UpperMetalTemp} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.LowerMetalTemp,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.LowerMetalTemp} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.CalculatedDewpoint,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CalculatedDewpoint} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.DewpointSpreadUpper,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.DewpointSpreadUpper} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.DewpointSpreadLower,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.DewpointSpreadLower} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.DewpointSpreadUtilized,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.DewpointSpreadUtilized} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.DewpointInhibitActive,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.DewpointInhibitActive} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.DewpointSpread,
        group: 'Environmentals',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.DewpointSpread} {...props} />,
    },
    //{
    //    id: 'Dewpoint Spread',
    //    group: 'Environmentals',
    //    field: 'ActiveDisplayStatus.LastKnownPoll?.DisplayStatusPoll?.EnvironmentalStatus?.DewpointSpread',
    //    render: ({ display, vm }) => (
    //        <DisplayCellWrapper vm={vm} cellId="Dewpoint Spread">
    //            {display?.environmentalsStatus?.dewpointSpread ?? '-'}
    //        </DisplayCellWrapper>
    //    ),
    //},
    //{
    //    id: 'Max Power Supply °C',
    //    group: 'Environmentals',
    //    field: 'ActiveDisplayStatus.LastKnownPoll?.DisplayStatusPoll?.EnvironmentalStatus?.MaxPowerSupplyTemp',
    //    render: ({ display, vm }) => (
    //        <DisplayCellWrapper vm={vm} cellId="Max Power Supply °C">
    //            {display?.environmentalsStatus?.maxPowerSupplyTemp ?? '-'}
    //        </DisplayCellWrapper>
    //    ),
    //},

    // GPS //
    {
        id: Mri.WebApi.MeasurementId.PerCentTimeGpsValid,
        group: 'GPS',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PerCentTimeGpsValid} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.GpsHasFix,
        group: 'GPS',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.GpsHasFix} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.GpsHDOP,
        group: 'GPS',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.GpsHDOP} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.GpsSatInView,
        group: 'GPS',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.GpsSatInView} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.GpsSatInFix,
        group: 'GPS',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.GpsSatInFix} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.GpsLatitude,
        group: 'GPS',
        render: props => (
            <MeasurementCell
                id={Mri.WebApi.MeasurementId.GpsLatitude}
                ignoreZero
                {...props}
                fallbackValue={
                    props.display?.configPoll?.geoVuConfig?.latitude
                        ? props.display?.configPoll?.geoVuConfig?.latitude + ' (Config)'
                        : props.display?.unitLocation?.latitude + ' (SmartVu)'
                }
            />
        ),
    },
    {
        id: Mri.WebApi.MeasurementId.GpsLongitude,
        group: 'GPS',
        render: props => (
            <MeasurementCell
                id={Mri.WebApi.MeasurementId.GpsLongitude}
                ignoreZero
                {...props}
                fallbackValue={
                    props.display?.configPoll?.geoVuConfig?.longitude
                        ? props.display?.configPoll?.geoVuConfig?.longitude + ' (Config)'
                        : props.display?.unitLocation?.longitude + ' (SmartVu)'
                }
            />
        ),
    },
    {
        id: Mri.WebApi.MeasurementId.GpsSignalStrength,
        group: 'GPS',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.GpsSignalStrength} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.GpsHasValidSerialData,
        group: 'GPS',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.GpsHasValidSerialData} {...props} />,
    },

    // INFINITETOUCH //
    {
        id: Mri.WebApi.MeasurementId.Sigma1CDAC,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sigma1CDAC} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.InfiniteTouch1Celsius1,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.InfiniteTouch1Celsius1} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.InfiniteTouch1Celsius2,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.InfiniteTouch1Celsius2} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma1FrameCount,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sigma1FrameCount} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma1FrameRate,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sigma1FrameRate} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma1MitigatingResetCount,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sigma1MitigatingResetCount} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma1NoisyFrequencyCount,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sigma1NoisyFrequencyCount} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma1RailedCount,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sigma1RailedCount} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma1RebaselineCount,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sigma1RebaselineCount} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma1SelfAmplitude,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sigma1SelfAmplitude} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma1TouchCount,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sigma1TouchCount} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma1TouchId,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sigma1TouchId} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma1Uptime,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell duration id={Mri.WebApi.MeasurementId.Sigma1Uptime} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma2CDAC,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sigma2CDAC} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.InfiniteTouch2Celsius1,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.InfiniteTouch2Celsius1} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.InfiniteTouch2Celsius2,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.InfiniteTouch2Celsius2} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma2FrameCount,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sigma2FrameCount} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma2FrameRate,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sigma2FrameRate} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma2MitigatingResetCount,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sigma2MitigatingResetCount} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma2NoisyFrequencyCount,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sigma2NoisyFrequencyCount} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma2RailedCount,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sigma2RailedCount} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma2RebaselineCount,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sigma2RebaselineCount} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma2SelfAmplitude,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sigma2SelfAmplitude} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma2TouchCount,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sigma2TouchCount} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma2TouchId,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Sigma2TouchId} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Sigma2Uptime,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell duration id={Mri.WebApi.MeasurementId.Sigma1Uptime} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.InfiniteTouch1GlassType,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell duration id={Mri.WebApi.MeasurementId.InfiniteTouch1GlassType} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.InfiniteTouch2GlassType,
        group: 'InfiniteTouch',
        render: props => <MeasurementCell duration id={Mri.WebApi.MeasurementId.InfiniteTouch2GlassType} {...props} />,
    },

    // VIDEO //
    {
        id: Mri.WebApi.MeasurementId.Video1RxState,
        group: 'Video',
        render: props => <VideoRXTXCell id={Mri.WebApi.MeasurementId.Video1RxState} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Video1TxState,
        group: 'Video',
        render: props => <VideoRXTXCell id={Mri.WebApi.MeasurementId.Video1TxState} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Video1InputState,
        group: 'Video',
        render: props => <VideoInputOutputCell id={Mri.WebApi.MeasurementId.Video1InputState} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Video1InputXRes,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Video1InputXRes} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Video1InputYRes,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Video1InputYRes} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Video1InputFramesPerSec,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Video1InputFramesPerSec} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Video1InputPixelClock,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Video1InputPixelClock} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Video1InputFullRangeDetected,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Video1InputFullRangeDetected} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Video1OutputState,
        group: 'Video',
        render: props => <VideoInputOutputCell id={Mri.WebApi.MeasurementId.Video1OutputState} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Video1OutputXRes,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Video1OutputXRes} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Video1OutputYRes,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Video1OutputYRes} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Video1OutputFramesPerSec,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Video1OutputFramesPerSec} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Video1OutputPixelClock,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Video1OutputPixelClock} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Video1OutputFullRangeDetected,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Video1OutputFullRangeDetected} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Video2InputRxState,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Video2InputRxState} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Video2InputState,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Video2InputState} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Video2InputXRes,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Video2InputXRes} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Video2InputYRes,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Video2InputYRes} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Video2InputFullRangeDetected,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Video2InputFullRangeDetected} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.Video2OutputFullRangeDetected,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Video2OutputFullRangeDetected} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.VideoRejuvenationModeActive,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.VideoRejuvenationModeActive} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.HdmiLinkQualityChan1,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.HdmiLinkQualityChan1} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.HdmiLinkQualityChan2,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.HdmiLinkQualityChan2} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.HdmiLinkQualityChan3,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.HdmiLinkQualityChan3} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.UrsaInputXRes,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.UrsaInputXRes} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.UrsaInputYRes,
        group: 'Video',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.UrsaInputYRes} {...props} />,
    },

    // ACCELEROMETER //
    {
        id: Mri.WebApi.MeasurementId.MemsRoll,
        group: 'Accelerometer',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.MemsRoll} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.MemsPitch,
        group: 'Accelerometer',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.MemsPitch} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.MemsShock,
        group: 'Accelerometer',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.MemsShock} {...props} />,
    },

    // CPU //
    {
        id: Mri.WebApi.MeasurementId.CPULoadOneMinuteAvgPct,
        group: 'CPU',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CPULoadOneMinuteAvgPct} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.CPULoadFiveMinuteAvgPct,
        group: 'CPU',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CPULoadFiveMinuteAvgPct} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.CPULoadTenMinuteAvgPct,
        group: 'CPU',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CPULoadTenMinuteAvgPct} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.MemoryTotalBytes,
        group: 'CPU',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.MemoryTotalBytes} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.MemoryFreeBytes,
        group: 'CPU',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.MemoryFreeBytes} {...props} />,
    },
    {
        id: Mri.WebApi.MeasurementId.MemoryUsedBytes,
        group: 'CPU',
        render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.MemoryUsedBytes} {...props} />,
    },

    /////////////
    // REMOVED //
    /////////////

    //{
    //    id: 'Display Alerts',
    //    field: 'DisplayAlertCount',
    //    render: props => <NewDisplayAlertCountCell {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.FrontDoorOpenStatus,
    //    group: 'Door',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.FrontDoorOpenStatus} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.RearDoorOpenStatus,
    //    group: 'Door',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.RearDoorOpenStatus} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.BottomRearDoorOpenStatus,
    //    group: 'Door',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.BottomRearDoorOpenStatus} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.SAMDoorOpenStatus,
    //    group: 'Door',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.SAMDoorOpenStatus} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.LeftDoorOpenStatus,
    //    group: 'Door',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.LeftDoorOpenStatus} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.RightDoorOpenStatus,
    //    group: 'Door',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.RightDoorOpenStatus} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.TotalAuxACAmps,
    //    group: 'Unit Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.TotalAuxACAmps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.TotalSolarBatteryChargeAmps,
    //    group: 'Unit Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.TotalSolarBatteryChargeAmps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.CCBattVolts,
    //    group: 'Unit Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CCBattVolts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.CCInputAmps,
    //    group: 'Unit Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CCInputAmps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.CCInputVolts,
    //    group: 'Unit Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CCInputVolts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.CCOuputAmps,
    //    group: 'Unit Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CCOuputAmps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.CCDailyOutputAmpHours,
    //    group: 'Unit Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CCDailyOutputAmpHours} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.CCDailyOutputKilowattHours,
    //    group: 'Unit Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CCDailyOutputKilowattHours} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PowerSupplyEfficiency,
    //    group: 'Unit Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PowerSupplyEfficiency} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.InvL1BuyAmps,
    //    group: 'System Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.InvL1BuyAmps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.InvL2BuyAmps,
    //    group: 'System Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.InvL2BuyAmps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.InvL1ChargeAmps,
    //    group: 'System Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.InvL1ChargeAmps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.InvL2ChargeAmps,
    //    group: 'System Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.InvL2ChargeAmps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.InvL1OutputAmps,
    //    group: 'System Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.InvL1OutputAmps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.InvL2OutputAmps,
    //    group: 'System Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.InvL2OutputAmps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.InvL1SellAmps,
    //    group: 'System Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.InvL1SellAmps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.InvL2SellAmps,
    //    group: 'System Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.InvL2SellAmps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.InvAC1L1Volts,
    //    group: 'System Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.InvAC1L1Volts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.InvAC1L2Volts,
    //    group: 'System Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.InvAC1L2Volts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.InvAC2L1Volts,
    //    group: 'System Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.InvAC2L1Volts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.InvAC2L2Volts,
    //    group: 'System Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.InvAC2L2Volts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.InvL1ACOutputVolts,
    //    group: 'System Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.InvL1ACOutputVolts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.InvL2ACOutputVolts,
    //    group: 'System Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.InvL2ACOutputVolts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.Psu12VAvgCelsius,
    //    group: 'System Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Psu12VAvgCelsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.FilteredInletVolts,
    //    group: 'System Power',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.FilteredInletVolts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsuAvgVolts,
    //    group: 'PSU 1',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsuAvgVolts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsuTotalAmps,
    //    group: 'PSU 1',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsuTotalAmps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsuTotalWatts,
    //    group: 'PSU 1',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsuTotalWatts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsuAvgCelsius,
    //    group: 'PSU 1',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsuAvgCelsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsuExtremeCelsius,
    //    group: 'PSU 1',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsuExtremeCelsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsu1Volts,
    //    group: 'PSU 1',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsu1Volts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsu1Amps,
    //    group: 'PSU 1',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsu1Amps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsu1Watts,
    //    group: 'PSU 1',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsu1Watts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsu1Celsius,
    //    group: 'PSU 1',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsu1Celsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.Psu1_12VCelsius,
    //    group: 'PSU 1',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Psu1_12VCelsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsu2Volts,
    //    group: 'PSU 2',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsu2Volts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsu2Amps,
    //    group: 'PSU 2',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsu2Amps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsu2Watts,
    //    group: 'PSU 2',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsu2Watts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsu2Celsius,
    //    group: 'PSU 2',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsu2Celsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.Psu2_12VCelsius,
    //    group: 'PSU 2',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Psu2_12VCelsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsu3Volts,
    //    group: 'PSU 3',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsu3Volts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsu3Amps,
    //    group: 'PSU 3',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsu3Amps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsu3Watts,
    //    group: 'PSU 3',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsu3Watts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsu3Celsius,
    //    group: 'PSU 3',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsu3Celsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.Psu3_12VCelsius,
    //    group: 'PSU 3',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Psu3_12VCelsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsu4Volts,
    //    group: 'PSU 4',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsu4Volts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsu4Amps,
    //    group: 'PSU 4',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsu4Amps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsu4Watts,
    //    group: 'PSU 4',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsu4Watts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.PrimaryPsu4Celsius,
    //    group: 'PSU 4',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.PrimaryPsu4Celsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.Psu4_12VCelsius,
    //    group: 'PSU 4',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Psu4_12VCelsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.CustomAuxPsu1Celsius,
    //    group: 'Custom PSU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CustomAuxPsu1Celsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.CustomAuxPsu2Celsius,
    //    group: 'Custom PSU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CustomAuxPsu2Celsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.CustomAuxPsu3Volts,
    //    group: 'Custom PSU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CustomAuxPsu3Volts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.CustomAuxPsu3Amps,
    //    group: 'Custom PSU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CustomAuxPsu3Amps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.CustomAuxPsu3Watts,
    //    group: 'Custom PSU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CustomAuxPsu3Watts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.CustomAuxPsu3Celsius,
    //    group: 'Custom PSU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CustomAuxPsu3Celsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.CustomAuxPsu4Volts,
    //    group: 'Custom PSU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CustomAuxPsu4Volts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.CustomAuxPsu4Amps,
    //    group: 'Custom PSU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CustomAuxPsu4Amps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.CustomAuxPsu4Watts,
    //    group: 'Custom PSU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CustomAuxPsu4Watts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.CustomAuxPsu4Celsius,
    //    group: 'Custom PSU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CustomAuxPsu4Celsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.OpenLoopCelsius,
    //    group: 'Fans',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.OpenLoopCelsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.FanBank4PwmLevel,
    //    group: 'Fans',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.FanBank4PwmLevel} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.FanBank5PwmLevel,
    //    group: 'Fans',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.FanBank5PwmLevel} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.Fan15RPM,
    //    group: 'Fans',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan15RPM} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.Fan16RPM,
    //    group: 'Fans',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan16RPM} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.Fan17RPM,
    //    group: 'Fans',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan17RPM} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.Fan18RPM,
    //    group: 'Fans',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Fan18RPM} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.Backlight1BlockDimmingPercentEnabled,
    //    group: 'Backlight',
    //    render: props => (
    //        <MeasurementCell id={Mri.WebApi.MeasurementId.Backlight1BlockDimmingPercentEnabled} {...props} />
    //    ),
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.Backlight2BlockDimmingPercentEnabled,
    //    group: 'Backlight',
    //    render: props => (
    //        <MeasurementCell id={Mri.WebApi.MeasurementId.Backlight2BlockDimmingPercentEnabled} {...props} />
    //    ),
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.Tile11SensorCelsius,
    //    group: 'Backlight',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile11SensorCelsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.Tile11LightSensor,
    //    group: 'Backlight',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile11LightSensor} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.Tile12SensorCelsius,
    //    group: 'Backlight',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile12SensorCelsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.Tile12LightSensor,
    //    group: 'Backlight',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Tile12LightSensor} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.Rain1hour,
    //    group: 'WeatherVu',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Rain1hour} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.Rain3hours,
    //    group: 'WeatherVu',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Rain3hours} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.Snow1hour,
    //    group: 'WeatherVu',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Snow1hour} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.Snow3hours,
    //    group: 'WeatherVu',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.Snow3hours} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.ELVPressureSensorStatus,
    //    group: 'Environmentals',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.ELVPressureSensorStatus} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.ExhaustSensorCelsius,
    //    group: 'Environmentals',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.ExhaustSensorCelsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.NexusBoardSensorCelsius,
    //    group: 'Environmentals',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.NexusBoardSensorCelsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.ReceiverBoardSensorCelsius,
    //    group: 'Environmentals',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.ReceiverBoardSensorCelsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.CoolerSensorCelsius,
    //    group: 'Environmentals',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.CoolerSensorCelsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.EvaporatorSensorCelsius,
    //    group: 'Environmentals',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.EvaporatorSensorCelsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.LcdCaseSensor1Celsius,
    //    group: 'Environmentals',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.LcdCaseSensor1Celsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.LcdCaseSensor2Celsius,
    //    group: 'Environmentals',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.LcdCaseSensor2Celsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.SpeedSensor,
    //    group: 'GPS',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.SpeedSensor} {...props} />,
    //},
    //// VTU //
    //{
    //    id: Mri.WebApi.MeasurementId.VtuInletVolts,
    //    group: 'VTU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.VtuInletVolts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.VtuInletAmps,
    //    group: 'VTU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.VtuInletAmps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.VtuInletWatts,
    //    group: 'VTU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.VtuInletWatts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.VtuVehicleVolts,
    //    group: 'VTU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.VtuVehicleVolts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.VtuVehicleWatts,
    //    group: 'VTU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.VtuVehicleWatts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.VtuVoltageDrop,
    //    group: 'VTU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.VtuVoltageDrop} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.BatteryVolts,
    //    group: 'VTU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.BatteryVolts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.BatteryAmps,
    //    group: 'VTU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.BatteryAmps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.TotalSolarSupplyWatts,
    //    group: 'VTU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.TotalSolarSupplyWatts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.TotalSolarLoadWatts,
    //    group: 'VTU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.TotalSolarLoadWatts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.SystemBatteryVolts,
    //    group: 'VTU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.SystemBatteryVolts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.OnUpsStatus,
    //    group: 'VTU',
    //    render: props => <MeasurementCell id={Mri.WebApi.MeasurementId.OnUpsStatus} {...props} />,
    //},

    /////////
    // OLD //
    /////////

    //{
    //    id: Mri.WebApi.MeasurementId.V48AvgCelsius,
    //    render: props => <SensorCell id={Mri.WebApi.MeasurementId.V48AvgCelsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.V48AvgVolts,
    //    render: props => <SensorCell id={Mri.WebApi.MeasurementId.V48AvgVolts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.V28AvgVolts,
    //    render: props => <SensorCell id={Mri.WebApi.MeasurementId.V28AvgVolts} {...props} />,
    //},

    //{
    //    id: Mri.WebApi.MeasurementId.V28AvgCelsius,
    //    render: props => <SensorCell id={Mri.WebApi.MeasurementId.V28AvgCelsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.V28Psu1Celsius,
    //    render: props => <SensorCell id={Mri.WebApi.MeasurementId.V28Psu1Celsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.V28Psu2Celsius,
    //    render: props => <SensorCell id={Mri.WebApi.MeasurementId.V28Psu2Celsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.V28Psu3Celsius,
    //    render: props => <SensorCell id={Mri.WebApi.MeasurementId.V28Psu3Celsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.V28Psu4Celsius,
    //    render: props => <SensorCell id={Mri.WebApi.MeasurementId.V28Psu4Celsius} {...props} />,
    //},

    //{
    //    id: Mri.WebApi.MeasurementId.TdPsuTotalAmps,
    //    render: props => <SensorCell id={Mri.WebApi.MeasurementId.TdPsuTotalAmps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.TdPsuTotalWatts,
    //    render: props => <SensorCell id={Mri.WebApi.MeasurementId.TdPsuTotalWatts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.TdPsuAvgCelsius,
    //    render: props => <SensorCell id={Mri.WebApi.MeasurementId.TdPsuAvgCelsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.TdPsu1Volts,
    //    render: props => <SensorCell id={Mri.WebApi.MeasurementId.TdPsu1Volts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.TdPsu1Amps,
    //    render: props => <SensorCell id={Mri.WebApi.MeasurementId.TdPsu1Amps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.TdPsu1Watts,
    //    render: props => <SensorCell id={Mri.WebApi.MeasurementId.TdPsu1Watts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.TdPsu1Celsius,
    //    render: props => <SensorCell id={Mri.WebApi.MeasurementId.TdPsu1Celsius} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.TdPsu2Volts,
    //    render: props => <SensorCell id={Mri.WebApi.MeasurementId.TdPsu2Volts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.TdPsu2Amps,
    //    render: props => <SensorCell id={Mri.WebApi.MeasurementId.TdPsu2Amps} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.TdPsu2Watts,
    //    render: props => <SensorCell id={Mri.WebApi.MeasurementId.TdPsu2Watts} {...props} />,
    //},
    //{
    //    id: Mri.WebApi.MeasurementId.TdPsu2Celsius,
    //    render: props => <SensorCell id={Mri.WebApi.MeasurementId.TdPsu2Celsius} {...props} />,
    //},
];
